import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { DATE_TIME_FORMAT, TIME_FORMAT } from '../../../../config/locale';
import { tzNormalizeDate } from '../../../../utils/date';
import { Table } from '../../../common/Table';
import {
  TIMETABLE_TEMPLATE_COLUMNS,
  TIMETABLE_TEMPLATE_WITH_REAL_TIME_COLUMNS,
} from '../../../../config/columns';
import { GEOFENCE_ALERT_TYPE } from '../../../../config/constants';

const TimetableTemplate = ({
  itineraryScheduleSegmentList,
  estimatedDepartureTime,
  showFullDate,
  isSegmentLevel,
  itineraryRealTimeList,
}) => {

  const [data, setData] = useState([]);

  const generateTimetableData = () => {
    const timetableData = [];

    const format = showFullDate ? DATE_TIME_FORMAT : TIME_FORMAT;

    itineraryScheduleSegmentList.forEach((row, index, array) => {
      const { segmentAdjustment: durationAdjustment, startOffset } = row;

      const duration = isSegmentLevel ? row.segment.duration : row.duration;

      const sourceLocationName = isSegmentLevel
        ? row.segment.sourceLocation.name
        : row.sourceLocationName;
      const destinationLocationName = isSegmentLevel
        ? row.segment.destinationLocation.name
        : row.destinationLocationName;

      const timetable = {
        sourceLocationName,
        destinationLocationName,
        departureTime: '-',
        duration,
        durationAdjustment,
        arrivalTime: '-',
        layover: 0,
      };

      if (estimatedDepartureTime) {
        timetable.departureTime = tzNormalizeDate({
          date: estimatedDepartureTime,
          addTime: { amount: startOffset, unit: 'minutes' },
          format,
        });

        timetable.arrivalTime = tzNormalizeDate({
          date: estimatedDepartureTime,
          addTime: {
            amount: startOffset + duration + durationAdjustment,
            unit: 'minutes',
          },
          format,
        });

        if (array[index + 1]) {
          const { startOffset: nextStartOffset } = array[index + 1];

          const layover =
            nextStartOffset - durationAdjustment - duration - startOffset;

          timetable.layover = layover;
        }
      }

      timetableData.push(timetable);
    });

    setData(timetableData);
  };

  useEffect(() => {
    if (itineraryScheduleSegmentList.length === 0) {
      setData([]);
      return;
    }
    generateTimetableData();
  }, []);

  useEffect(() => {
    if (
      itineraryRealTimeList &&
      itineraryRealTimeList.length > 0 &&
      data.length > 0 &&
      !data.some(
        (item) => item.realTimeDepartureTime || item.realTimeArrivalTime,
      )
    ) {
      const updatedData = data.map((item) => {
        const newItem = { ...item };
        const { sourceLocationName, destinationLocationName } = newItem;

        const realTimeData = itineraryRealTimeList
          .filter(
            (realTimeItem) =>
              realTimeItem.location.name === sourceLocationName &&
              GEOFENCE_ALERT_TYPE[realTimeItem.geofenceAlertType].value ===
                GEOFENCE_ALERT_TYPE.GEOFENCE_EXIT.value,
          )
          .reduce((earliest, current) => {
            if (earliest === null || current.timestamp > earliest.timestamp)
              return current;
            return earliest;
          }, null);

        const realTimeDestinationData = itineraryRealTimeList
          .filter(
            (realTimeItem) =>
              realTimeItem.location.name === destinationLocationName &&
              GEOFENCE_ALERT_TYPE[realTimeItem.geofenceAlertType].value ===
                GEOFENCE_ALERT_TYPE.GEOFENCE_ENTRY.value,
          )
          .reduce((latest, current) => {
            if (latest === null || current.timestamp < latest.timestamp)
              return current;
            return latest;
          }, null);


        if (realTimeData) {
          newItem.realTimeDepartureTime = tzNormalizeDate({
            date: realTimeData.timestamp,
            format: DATE_TIME_FORMAT,
          });
        } else {
          newItem.realTimeDepartureTime = '-';
        }

        if (realTimeDestinationData) {
          newItem.realTimeArrivalTime = tzNormalizeDate({
            date: realTimeDestinationData.timestamp,
            format: DATE_TIME_FORMAT,
          });
        } else {
          newItem.realTimeArrivalTime = '-';
        }

        return newItem;
      });
      setData(updatedData);
    }
  }, [itineraryRealTimeList, data.length]);

  const columns =
    itineraryRealTimeList != null
      ? TIMETABLE_TEMPLATE_WITH_REAL_TIME_COLUMNS
      : TIMETABLE_TEMPLATE_COLUMNS;

  const timetable = (
    <Table
      columns={columns}
      data={data}
      defaultPageSize={data.length}
      showPagination={false}
    />
  );

  return timetable;
};

TimetableTemplate.propTypes = {
  itineraryScheduleSegmentList: PropTypes.arrayOf(PropTypes.instanceOf(Object))
    .isRequired,
  estimatedDepartureTime: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.instanceOf(Date),
  ]).isRequired,
  showFullDate: PropTypes.bool,
  isSegmentLevel: PropTypes.bool,
  itineraryRealTimeList: PropTypes.arrayOf(PropTypes.instanceOf(Object)),
};

TimetableTemplate.defaultProps = {
  showFullDate: true,
  isSegmentLevel: true,
  itineraryRealTimeList: null,
};

export default TimetableTemplate;
